import React from "react";
import style from "./ProcedureBlock.module.scss";
import { approachesFor, typeByKey } from "../debridementRules";
import { SelectBox } from "../../../../../primary/SelectBox/selectBox";
import {
  DEBRIDEMENT_TYPES,
  DEPTH_OF_DEBRIDEMENT,
  INSTRUMENTS_USED,
  NECROTIC_SUBCUTANEOUS_TISSUE,
  NON_TISSUE_REMOVED,
  TECHNIQUE_USED,
} from "../debridementConfig";
import { MultiSelect } from "../../../../../primary/MultiSelect/multiSelect";
import { TextField } from "../../../../../primary";
import { ChipGroup } from "../../../../../secondary/ChipGroup/ChipGroup";

export const ProcedureBlock = ({
  form,
  showApproach,
  showSubcutaneous,
  disabled,
  onField,
  onToggleMulti,
  register,
  errors,
  necroticTissueRef,
  instrumentsUsedRef,
  onMultiSelect,
}) => {
  const type = typeByKey(form.surgicalDebridementProcedure);

  return (
    <section className={`${style.form_section} ${disabled ? style.block_off : ""}`}>
      <div className={style.form_section_header}>
        <span className={style.form_section_number}>2</span>
        <span className={style.form_section_title}>Procedure</span>
      </div>

      <div className={style.form_section_body}>
        {/* Type of debridement */}
        <div className={style.form_field}>
          <label className={style.form_field_label} htmlFor="surgicalDebridementProcedure">
            What type of debridement? <span className={style.form_astrick}>*</span>
          </label>
          <SelectBox
            id="surgicalDebridementProcedure"
            name="surgicalDebridementProcedure"
            optionValue={DEBRIDEMENT_TYPES}
            defaultValue={form.surgicalDebridementProcedure || ""}
            placeholder="Select…"
            disabled={disabled}
            errors={errors}
            classes={{ inputClassName: style.custom_basic_select }}
            onChange={(e) => onField("surgicalDebridementProcedure", e.target.value)}
            validator={register({
              required: "This field is required",
            })}
          />

          {showApproach && (
            <div className={style.sublist}>
              <div className={style.sublist_cap}>
                {type?.approachLabel || "Approach"} <span className={style.form_astrick}>*</span>
              </div>
              <SelectBox
                id="nonExcisionalApproach"
                name="nonExcisionalApproach"
                optionValue={approachesFor(form.surgicalDebridementProcedure)}
                defaultValue={form.nonExcisionalApproach || ""}
                placeholder="Select…"
                disabled={disabled}
                classes={{ inputClassName: style.custom_basic_select }}
                onChange={(e) => onField("nonExcisionalApproach", e.target.value)}
                validator={register({
                  required: "This field is required",
                })}
                errors={errors}
              />
            </div>
          )}
        </div>

        {/* Deepest tissue removed */}
        <div className={style.form_field}>
          <label className={style.form_field_label} htmlFor="depthOfDebridement">Determine deepest tissue removed</label>
          <SelectBox
            id="depthOfDebridement"
            name="depthOfDebridement"
            optionValue={DEPTH_OF_DEBRIDEMENT}
            defaultValue={form.depthOfDebridement || ""}
            placeholder="Select…"
            disabled={disabled}
            classes={{ inputClassName: style.custom_basic_select }}
            onChange={(e) => onField("depthOfDebridement", e.target.value)}
          />

          {showSubcutaneous && (
            <div className={style.sublist}>
              <div className={style.sublist_cap}>Subcutaneous tissue — specify</div>
              <ChipGroup
                name="necroticSubcutaneousTissue"
                options={NECROTIC_SUBCUTANEOUS_TISSUE}
                selected={form.necroticSubcutaneousTissue}
                disabled={disabled}
                onToggle={(k) => onToggleMulti("necroticSubcutaneousTissue", k)}
                multiple
              />
            </div>
          )}
        </div>

        {/* Other non-tissue types removed */}
        <div className={style.form_field}>
          <label className={style.form_field_label}>Other non-tissue types removed</label>
          <MultiSelect
            id="necroticTissue"
            name="necroticTissue"
            classes={{ inputClassName: style.custom_basic_select }}
            optionValue={NON_TISSUE_REMOVED}
            showCheckbox={true}
            displayValue="value"
            validator={(e) => {
              register({ required: false });
              necroticTissueRef.current = e;
            }}
            onSelect={(option) => onMultiSelect("necroticTissue", option, necroticTissueRef)}
            onRemove={(option) => onMultiSelect("necroticTissue", option, necroticTissueRef)}
            defaultValue={form.necroticTissue ?? []}
            disabled={disabled}
          />
        </div>

        {/* Instruments */}
        <div className={style.form_field}>
          <label className={style.form_field_label}>Select instruments used</label>
          <MultiSelect
            id="instrumentsUsed"
            name="instrumentsUsed"
            classes={{ inputClassName: style.custom_basic_select }}
            optionValue={INSTRUMENTS_USED}
            showCheckbox={true}
            displayValue="value"
            validator={(e) => {
              register({ required: false });
              instrumentsUsedRef.current = e;
            }}
            onSelect={(option) => onMultiSelect("instrumentsUsed", option, instrumentsUsedRef)}
            onRemove={(option) => onMultiSelect("instrumentsUsed", option, instrumentsUsedRef)}
            defaultValue={form.instrumentsUsed ?? []}
            disabled={disabled}
          />
        </div>

        {/* Surface area */}
        <div className={style.form_field}>
          <label className={style.form_field_label}>Document total surface area debrided</label>
          <div className={style.row2}>
            <TextField
              type="number"
              id="surfaceAreaOfNecroticSkinTissue"
              name="surfaceAreaOfNecroticSkinTissue"
              label="Necrotic Skin (cm²)"
              value={form.surfaceAreaOfNecroticSkinTissue || ""}
              disabled={disabled}
              validator={register()}
              errors={errors}
              classes={{ labelClass: style.form_field_label, inputClassName: style.basic_input }}
              onChange={(e) => onField("surfaceAreaOfNecroticSkinTissue", e.target.value)}
            />
            <TextField
              type="number"
              id="surfaceAreaOfNecroticSubqTissue"
              name="surfaceAreaOfNecroticSubqTissue"
              label="Necrotic Subcutaneous Tissue (cm²)"
              value={form.surfaceAreaOfNecroticSubqTissue || ""}
              disabled={disabled}
              validator={register()}
              errors={errors}
              classes={{ labelClass: style.form_field_label, inputClassName: style.basic_input }}
              onChange={(e) => onField("surfaceAreaOfNecroticSubqTissue", e.target.value)}
            />
          </div>
        </div>

        {/* Technique */}
        <div className={style.form_field}>
          <label className={style.form_field_label} htmlFor="techniqueUsed">Technique used</label>
          <SelectBox
            id="techniqueUsed"
            name="techniqueUsed"
            optionValue={TECHNIQUE_USED}
            defaultValue={form.techniqueUsed || ""}
            placeholder="Select…"
            disabled={disabled}
            classes={{ inputClassName: style.custom_basic_select }}
            onChange={(e) => onField("techniqueUsed", e.target.value)}
          />
        </div>
      </div>
    </section>
  );
};
